import { useNavigate } from 'react-router-dom';

import React from 'react';
import { reverse } from 'named-urls';
import { GridEventListener, GridRowParams, GridValidRowModel } from '@mui/x-data-grid';

import routes from 'core/routes';

import Grid, { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { ListRendererType } from 'components/Grid/renderers/GridListRenderer/types';
import { listRenderer } from 'components/Grid/renderers/GridListRenderer';
import { Container as GridChipContainer } from 'components/Grid/renderers/GridChipRenderer/styles';

import Search from 'components/Grid/operators/Search';
import API from 'utils/api/api';

import Chip from 'components/Chip';

import { UuidableName } from 'utils/api/types';

import { VatixError } from 'components/Error/types';

import DeviceTypeCell from '../DeviceTypeCell';

const basicColumns = [
  {
    field: 'type',
    headerName: 'Device',
    filterOperators: Search(API.devices.searchDevices, API.devices.loadDeviceDetails, 'customId'),
    renderCell: DeviceTypeCell,
    valueGetter: (value: unknown, row: GridValidRowModel) => ({ type: value, customId: row.customId }),
    nativeFilter: 'devices',
    // ToDo: implement sorting on backend
    sortable: false,
  },
  {
    field: 'tags',
    headerName: 'Device tags',
    filterOperators: Search(API.devices.searchDeviceTags, API.devices.deviceTagsDetails, 'name'),
    renderCell: listRenderer(
      ((props) => (
        <GridChipContainer>
          <Chip
            label={(props.value.value as UuidableName).name}
            size="small"
            variant="filled"
            colors={{ background: 'rgba(7, 114, 229, 0.08)', foreground: 'rgba(1, 96, 199, 1)' }}
          />
        </GridChipContainer>
      )) as ListRendererType,
      3,
      undefined,
      { empty: '-', customEmptyText: '-' }
    ),
    nativeFilter: 'device_tags',
    sortable: false,
  },
  {
    defaultVisibility: false,
    field: 'groups',
    headerName: 'Teams',
    filterOperators: Search(API.searchGroups, API.teams.loadTeamDetails, 'name'),
    renderCell: listRenderer(defaultRenderers.short_text as ListRendererType, 3, (values: unknown[]) =>
      values.join(', ')
    ),
    valueGetter: (value: unknown) => (value as UuidableName[]).map((item: UuidableName) => item.name),
    nativeFilter: 'teams',
    sortable: false,
  },
  {
    field: 'user',
    headerName: 'Current user',
    filterOperators: defaultOperators.user(),
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    valueGetter: (value: unknown) => ({ value }),
    nativeFilter: 'users',
    // ToDo: implement sorting on backend
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const DeviceList: React.FunctionComponent<{ onError: (error: VatixError) => void }> = ({
  onError,
}): React.ReactElement => {
  const navigate = useNavigate();
  const onRowClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    navigate(reverse(routes.dashboard.alarms.devices.details, { deviceId: task.row.uuid }));
  };

  return (
    <Grid
      basicColumns={basicColumns}
      entity="device"
      onError={onError}
      onRowClick={onRowClick}
      dataURL="/api/devices/"
    />
  );
};

export default DeviceList;
